import styled from "@emotion/styled"
import { useDealerContext } from "@porsche-customer/slfinrtl-context"
import { isLanguage } from "@porsche-customer/slfinrtl-context-types"
import { PButtonPure, PIcon, PInputText } from "@porsche-design-system/components-react"
import type { PInputTextProps, InputTextInputEventDetail } from "@porsche-design-system/components-react"
import { colorCanvas, colorPrimary } from "@porsche-design-system/components-react/emotion"
import flatpickrLocalesModule from "flatpickr/dist/l10n"
import monthSelectPlugin from "flatpickr/dist/plugins/monthSelect"
import type { key as FlatpickrKey } from "flatpickr/dist/types/locale"
import { forwardRef, useEffect, useMemo, useRef, useState } from "react"
import type { MouseEvent } from "react"
import type DatePicker from "react-flatpickr"
import FlatpickrModule from "react-flatpickr"
import { Tooltip } from "../../tooltip/Tooltip"
import { FlatpickrWrapper } from "../datepicker/FlatpickrWrapper"
import { createTopLayerCalendar } from "../datepicker/topLayerCalendar"

// workarounds for outdated package needed since vite 8
const Flatpickr = (FlatpickrModule as any).default ?? FlatpickrModule

// unlike above, we cannot check for ".default" here, because the flatpickr locales object contains a locale called "default" 😅
const isFlatpickrLocalesObject =
	typeof flatpickrLocalesModule === "object" && flatpickrLocalesModule !== null && "ar" in flatpickrLocalesModule
const flatpickrLocales = isFlatpickrLocalesObject
	? flatpickrLocalesModule
	: (flatpickrLocalesModule as unknown as { default: typeof flatpickrLocalesModule }).default

export interface MonthpickerProps {
	/**
	 * When true, shows a clear control next to the calendar icon while a value is set.
	 * Defaults to false so existing call sites keep their current UX.
	 */
	clearable?: boolean

	/**
	 * Accessible label for the clear control. Required for a meaningful name when `clearable` is true.
	 */
	clearButtonLabel?: string

	/**
	 * Controls if the monthpicker is disabled.
	 */
	disabledHint?: string

	/**
	 * Show or hide label and description text.
	 * For better accessibility it is recommended to show the label.
	 */
	hideLabel?: boolean

	/**
	 * Will be displayed as a floating label in the Input field
	 */
	label?: string

	name: string

	/**
	 * Will be displayed underneath the label in a smaller font
	 */
	description?: string

	/**
	 * The latest locale-date to select
	 * Hint:
	 *    the month is included if you pass in the first day of that month
	 *    if you pass in the second day of the month, that month gets excluded.
	 * Example:
	 * - If you want to include January 2024, pass in `new Date(2024, 0, 1)` (month is 0-indexed)
	 * - If you want to exclude January 2024, pass in `new Date(2024, 0, 2)` (month is 0-indexed)
	 */
	maxDate?: Date

	/** The message styled depending on validation state. */
	message?: string

	/**
	 * The oldest locale-date to select
	 * Hint:
	 *    the month is included if you pass in the first day of that month
	 *    if you pass in the second day of the month, that month gets excluded.
	 * Example:
	 * - If you want to include January 2024, pass in `new Date(2024, 0, 1)` (month is 0-indexed)
	 * - If you want to exclude January 2024, pass in `new Date(2024, 0, 2)` (month is 0-indexed)
	 *
	 */
	minDate?: Date

	/**
	 * Callback that is called when the input field is blurred. Can be used to check if the entered or selected date is valid.
	 */
	onBlur?: PInputTextProps["onBlur"]

	/**
	 * Callback that is called when the entered or selected date is valid
	 */
	onChange?: (value: { input: string; isoDate: string | null; isValid: boolean }) => void

	/**
	 * Whether this is a required input or not. Will be indicated by a star next to the label. Default false
	 */
	required?: boolean

	/**
	 * The validation state.
	 * @default "none"
	 */
	state?: "none" | "error" | "success"

	/**
	 * The value with which the monthpicker can be initialized
	 */
	value?: { month: number; year: number } | null
}

/**
 * A Monthpicker allows the user to select a date from a calendar.
 * This thin Monthpicker wrapper uses https://github.com/flatpickr/flatpickr under the hood.
 */
export const Monthpicker = forwardRef<HTMLInputElement, MonthpickerProps>((props, inputRef) => {
	const {
		clearable = false,
		clearButtonLabel,
		description,
		disabledHint,
		hideLabel,
		label,
		maxDate,
		message,
		minDate,
		name,
		onBlur,
		onChange,
		required,
		state,
		value,
		...rest
	} = props

	const { locale } = useDealerContext()

	const language = useMemo(() => {
		const contextLanguage = locale.split("-")[0]
		return isLanguage(contextLanguage) ? contextLanguage : "en"
	}, [locale])

	const flatPickrRef = useRef<DatePicker>(null)
	const topLayerCalendar = useMemo(() => createTopLayerCalendar(), [])

	useEffect(() => topLayerCalendar.stop, [topLayerCalendar])

	const lang = useMemo(
		() =>
			flatpickrLocales[language as FlatpickrKey] ??
			flatpickrLocales[navigator.language.split("-")[0] as FlatpickrKey] ??
			flatpickrLocales.default,
		[language],
	)

	const initialDate = useMemo(() => {
		if (!value) {
			return undefined
		}
		// Convert { month: 1-12, year } to Date object
		return new Date(value.year, value.month - 1, 1)
	}, [value])

	const [inputValue, setInputValue] = useState<string | undefined>(() => {
		if (!initialDate) {
			return undefined
		}
		const monthName = lang.months.longhand[initialDate.getMonth()]
		return `${monthName} ${initialDate.getFullYear()}`
	})

	const onFlatpickrChange = (_dates: Date[], currentDateString: string) => {
		const selectedDate = _dates[0]
		if (!onChange || !selectedDate || !currentDateString) {
			return
		}

		const yearMonthIsoString = selectedDate.toISOString()

		onChange({
			input: currentDateString,
			isoDate: yearMonthIsoString,
			isValid: true,
		})
		setInputValue(currentDateString)
	}

	const onInputChange = (_event: CustomEvent<InputTextInputEventDetail>) => {
		// we disallow the user to change the input directly, but still allow to copy the text
		// this UI/UX is not optimal, but good enough for now
		return
	}

	const onWrapperClick = () => {
		if (disabledHint) {
			return
		}

		flatPickrRef.current?.flatpickr?.open()
	}

	const showClearButton = clearable && !!inputValue && !disabledHint

	const onClearClick = (event: MouseEvent) => {
		event.stopPropagation()
		event.preventDefault()

		setInputValue(undefined)
		onChange?.({
			input: "",
			isoDate: null,
			isValid: true,
		})
		flatPickrRef.current?.flatpickr?.clear()
	}

	return (
		<StyledFlatpickrWrapper {...rest} onClick={onWrapperClick}>
			<Flatpickr
				ref={flatPickrRef}
				value={initialDate}
				disabled={!!disabledHint}
				onChange={onFlatpickrChange}
				onReady={topLayerCalendar.onReady}
				onOpen={topLayerCalendar.onOpen}
				onClose={topLayerCalendar.onClose}
				options={{
					allowInput: false,
					clickOpens: false,
					// Native <input type="month"> on touch devices is position:absolute + translated
					// off-screen, which makes Playwright treat #storybook-root as not visible on Pixel 5.
					disableMobile: true,
					locale: lang,
					maxDate,
					minDate,
					plugins: [
						monthSelectPlugin({
							altFormat: "F Y",
							dateFormat: "F Y",
							shorthand: false,
						}),
					],
					position: topLayerCalendar.position,
					static: true,
					wrap: true,
				}}
			>
				{/* dummy element to make flatpickr happy, since data-input does not work on pds web component */}
				<input type="text" hidden data-input />

				<Tooltip content={disabledHint}>
					<PInputText
						ref={inputRef}
						name={name}
						description={description}
						label={label}
						hideLabel={hideLabel}
						state={state}
						message={message}
						value={inputValue ?? ""}
						autoComplete="off"
						required={required}
						disabled={!!disabledHint}
						onInput={onInputChange}
						onBlur={onBlur}
					>
						<span slot="end">
							{showClearButton ? (
								<PButtonPure
									type="button"
									icon="close"
									hideLabel
									data-testid={`${name}-clear-button`}
									onClick={onClearClick}
								>
									{clearButtonLabel}
								</PButtonPure>
							) : null}
							<PIcon name="calendar" color="contrast-medium" aria-hidden />
						</span>
					</PInputText>
				</Tooltip>
			</Flatpickr>
		</StyledFlatpickrWrapper>
	)
})

Monthpicker.displayName = "Monthpicker"

// copied and adjusted from node_modules/flatpickr/dist/plugins/monthSelect/style.css
const StyledFlatpickrWrapper = styled(FlatpickrWrapper)`
	.flatpickr-monthSelect-months {
		margin: 0;
		flex-wrap: wrap;
	}

	.flatpickr-monthSelect-month {
		background: none;
		border: 1px solid transparent;
		border-radius: 0;
		box-sizing: border-box;
		color: #404848;
		cursor: pointer;
		display: inline-block;
		font-weight: 400;
		justify-content: center;
		padding: 10px;
		position: relative;
		-webkit-box-pack: center;
		-webkit-justify-content: center;
		-ms-flex-pack: center;
		text-align: center;
		width: 33%;

		&.selected {
			background: ${colorPrimary};
			border-color: ${colorPrimary};
			font-weight: bold;

			&:hover {
				color: ${colorCanvas};
				background: ${colorPrimary};
				border-color: ${colorPrimary};
				font-weight: bold;
			}
		}

		&:hover {
			background: ${colorCanvas};
			border-color: ${colorPrimary};
			color: ${colorPrimary};
		}
	}

	.flatpickr-monthSelect-month.flatpickr-disabled,
	.flatpickr-monthSelect-month.flatpickr-disabled:hover {
		cursor: not-allowed;
		color: rgba(64, 72, 72, 0.1);
		border-color: ${colorCanvas};
	}

	.flatpickr-monthSelect-month.flatpickr-disabled:focus {
		color: rgba(64, 72, 72, 0.3);
		cursor: not-allowed;
		background: none !important;
	}

	.flatpickr-monthSelect-month.today {
		border-color: ${colorCanvas};
		border-bottom-color: #f64747;
		&.selected {
			border-color: ${colorPrimary};
		}
	}

	.flatpickr-monthSelect-month.inRange,
	.flatpickr-monthSelect-month.inRange.today {
		background: ${colorCanvas};
		border-color: ${colorPrimary};
	}

	.flatpickr-monthSelect-month.today:hover,
	.flatpickr-monthSelect-month.today:focus {
		border-color: #f64747;
		background: #f64747;
		color: ${colorCanvas};
	}

	.flatpickr-monthSelect-month.startRange,
	.flatpickr-monthSelect-month.endRange {
		box-shadow: none;
		background: ${colorPrimary};
		color: ${colorCanvas};
		border-color: ${colorPrimary};
	}

	.flatpickr-monthSelect-month.startRange {
		border-radius: 50px 0 0 50px;
	}

	.flatpickr-monthSelect-month.endRange {
		border-radius: 0 50px 50px 0;
	}

	.flatpickr-monthSelect-month.startRange.endRange {
		border-radius: 50px;
	}

	.flatpickr-monthSelect-month.inRange {
		border-radius: 0;
		box-shadow: -5px 0 0 #e6e6e6, 5px 0 0 #e6e6e6;
	}
`
